/**
 * Impact measurement — the per-module benefit reading, as pure functions over the generated
 * types. bata-ui#132 step 5.
 *
 * This module is the ONE place the product derives "what did the cycle say it was worth" per
 * module. `/strategic/impact` renders it, and the Cockpit's impact strip (`src/app/routes/
 * overview/`) imports it rather than re-deriving it — the shell may compose a chapter, and two
 * derivations of the same money would agree only by discipline. See handoff H1.
 *
 * ── WHAT IT READS, AND ONLY THAT ─────────────────────────────────────────────
 *
 * Every figure is a sum of a field the platform STORES per run and serves from a read whose
 * reader `bata-platform`'s `services/api/wiring.py` assigns (checked on `origin/main`
 * 2026-09-26: `InSeasonReader.runs`, `.price_actions`; `ExitReader.consolidation_runs`,
 * `.consolidation_lines`). A route whose reader is never assigned answers empty forever, and a
 * KPI built on one would read "nothing happened" when the truth is "nothing is wired".
 *
 *   in-season  `PriceActionSummary.expected_margin_recovery`, summed per cycle run ONCE PER
 *              PATH (`GET /inseason/price-actions?run_id=`). EXPECTED, not realised: it is
 *              what the optimiser projects for the paths it recommended. The field is the
 *              path's total contribution, written by `price_cycle` on EVERY step of the path
 *              (bata-platform `services/jobs/inseason.py`), so a sum over steps counted a
 *              four-step path four times — see `pricePathsOf`.
 *   exit       `ConsolidationLineSummary.net_benefit_amount` and `incremental_revenue_amount`,
 *              summed over the PROPOSED, fully-priced lanes of one `exit.consolidation_run`
 *              (`GET /exit/consolidation/lines?run_id=`), with each lane's `revenue_basis`
 *              carried beside the sum rather than folded into it.
 *
 * ── WHAT IT REFUSES TO PRODUCE ───────────────────────────────────────────────
 *
 * Every refusal is an `absent` figure with a reason key, never a zero (ADR 0014):
 *
 *   * **Money in two currencies does not add.** A run whose rows name more than one currency
 *     has no total; the currencies are named instead.
 *   * **An empty run is not a zero benefit.** A run that recommended no price step, or
 *     proposed no lane, has nothing to measure — which is a different finding from "measured,
 *     and it was nought".
 *   * **A lane without all three amounts is in none of the sums** — exit's own rule
 *     (`consolidationPotential`), restated here because a chapter may not import another
 *     chapter. Three totals over three different subsets of lanes would not reconcile.
 *   * **`total_contribution` and `carrying_cost` are not stored per run.** They are fields of
 *     a LIVE solve (`GET /inseason/markdown/path`), one option × cluster at a time; the estate
 *     keeps no per-run total of either. `NOT_STORED_PER_RUN` says so.
 *   * **`median_net_benefit` is a per-market median** (gate D2's evidence, in-season transfers)
 *     and medians do not add across markets. `PER_MARKET_MEDIAN` says so.
 */

import type {
  ConsolidationLineSummary, ConsolidationRunSummary, CycleRunSummary, PriceActionSummary,
} from '@/api/generated';
import type { Datum } from '@/charts';

// ── the shape of a figure ───────────────────────────────────────────────────

/**
 * One module-level amount. Measured, in exactly one currency — or absent, with the reason.
 *
 * There is no third state and no default: a caller that renders a `Measure` passes
 * `figure.kind === 'measured' ? figure.amount : null` and `absent: { labelKey, reasonKey:
 * figure.reasonKey }`, which is `impactMeasureProps` below.
 */
export type ImpactFigure =
  | { readonly kind: 'measured'; readonly amount: number; readonly currency: string }
  | {
    readonly kind: 'absent';
    /** ICU key in the `strategic` namespace saying why. */
    readonly reasonKey: string;
    /** The currencies that could not be added, when that is the reason. Sorted. */
    readonly currencies?: readonly string[];
  };

/** The catalogue key every absent impact figure is announced by. */
export const ABSENT_IMPACT_LABEL = 'strategic.absent.label';

/** Why a figure is absent — the full vocabulary, so a test can hold every key to a message. */
export const IMPACT_ABSENCE = {
  noRun: 'strategic.absent.noRun',
  noPriceSteps: 'strategic.absent.noPriceSteps',
  noProposedLanes: 'strategic.absent.noProposedLanes',
  noPricedLanes: 'strategic.absent.noPricedLanes',
  mixedCurrencies: 'strategic.absent.mixedCurrencies',
  tooManyLines: 'strategic.absent.tooManyLines',
  notStoredPerRun: 'strategic.absent.notStoredPerRun',
  perMarketMedian: 'strategic.absent.perMarketMedian',
} as const;

const absent = (reasonKey: string, currencies?: readonly string[]): ImpactFigure => (
  currencies === undefined ? { kind: 'absent', reasonKey } : { kind: 'absent', reasonKey, currencies }
);

/** `Measure`'s `value` and `absent` for a figure — the one way to render one. */
export function impactMeasureProps(figure: ImpactFigure): {
  readonly value: number | null;
  readonly absent: { readonly labelKey: string; readonly reasonKey: string };
} {
  return figure.kind === 'measured'
    ? { value: figure.amount, absent: { labelKey: ABSENT_IMPACT_LABEL, reasonKey: IMPACT_ABSENCE.noRun } }
    : { value: null, absent: { labelKey: ABSENT_IMPACT_LABEL, reasonKey: figure.reasonKey } };
}


// ── decimals ────────────────────────────────────────────────────────────────

/**
 * A decimal string into a number, or `null` — never `0`. The platform sends `decimal(20, 6)`
 * as a string so JSON does not round it. Exit's `parseDecimal` is the same rule; a chapter may
 * not import another chapter, so it is restated rather than shared.
 */
export function parseDecimal(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Sum amounts that must share one currency, or say why they cannot be summed. */
function sumInOneCurrency(
  entries: readonly { readonly currency: string; readonly amount: number }[],
  emptyReason: string,
): ImpactFigure {
  if (entries.length === 0) return absent(emptyReason);
  const currencies = [...new Set(entries.map((entry) => entry.currency))].sort();
  if (currencies.length > 1) return absent(IMPACT_ABSENCE.mixedCurrencies, currencies);
  return {
    kind: 'measured',
    amount: entries.reduce((sum, entry) => sum + entry.amount, 0),
    currency: currencies[0] as string,
  };
}

// ── in-season ───────────────────────────────────────────────────────────────

/** One cycle run's impact reading. */
export interface InseasonRunImpact {
  readonly runId: string;
  readonly generatedAt: string;
  /**
   * When the run ran: its cycle tick, else when it was recorded, else `generatedAt`. `generatedAt` is the
   * PERIOD END, the same for every run of a period, so a chart or a list of runs keyed on it told them
   * apart by an identifier (audit 2, B).
   */
  readonly ranAt: string;
  readonly countryKey: string;
  readonly periodKey: string;
  /** `CycleRunSummary.stock_partition_is_fresh` — a run on a quarantined partition says so. */
  readonly freshStock: boolean;
  /** Price steps the run recommended. */
  readonly priceSteps: number;
  /** Paths the run recommended (article × cluster) — the rows the recovery is summed over. */
  readonly pricePaths: number;
  /** Σ `expected_margin_recovery` over the run's PATHS, each once, one currency. */
  readonly expectedMarginRecovery: ImpactFigure;
}

/**
 * The run's price steps folded to one row per PATH.
 *
 * A path is one `(option_id, cluster_id)` problem of the run: `price_cycle` poses them per
 * that pair and derives each step's id from `(run_id, option_id, cluster_id, week)`; the
 * channel is the cycle's, one per run. So within one run's read the key is
 * `article_key × cluster_key` (channel carried, constant). Every step of a path carries the
 * path's `expected_margin_recovery`, so the first step stands for the path.
 *
 * Written here rather than imported: a chapter may not import another chapter, and the
 * in-season chapter derives the same fold in `markdown-calendar.ts`.
 */
export function pricePathsOf(actions: readonly PriceActionSummary[]): readonly PriceActionSummary[] {
  const byPath = new Map<string, PriceActionSummary>();
  for (const action of actions) {
    const key = `${action.article_key}\u0000${action.cluster_key}\u0000${action.channel_key}`;
    if (!byPath.has(key)) byPath.set(key, action);
  }
  return [...byPath.values()];
}

/** DERIVE — one cycle run's expected margin recovery. */
export function inseasonRunImpact(
  run: CycleRunSummary,
  actions: readonly PriceActionSummary[],
): InseasonRunImpact {
  const paths = pricePathsOf(actions);
  return {
    runId: run.run_id,
    generatedAt: run.generated_at,
    ranAt: run.cycle_tick ?? run.recorded_at ?? run.generated_at,
    countryKey: run.country_key,
    periodKey: run.period_key,
    freshStock: run.stock_partition_is_fresh,
    priceSteps: actions.length,
    pricePaths: paths.length,
    // Once per PATH, never per step — see `pricePathsOf`.
    expectedMarginRecovery: sumInOneCurrency(
      paths.map((path) => ({ currency: path.currency_code, amount: path.expected_margin_recovery })),
      IMPACT_ABSENCE.noPriceSteps,
    ),
  };
}

/**
 * Which run the in-season headline is about (F16, bata-ui#145).
 *
 * `GET /inseason/runs` lists EVERY `inseason_run` of a cycle — its distribute, price and
 * rebalance runs alike — with no field saying which kind a row is, and a `generated_at` that is
 * the window end and so the same for all of them. Only a price run writes price actions. The
 * headline used to read `runs[0]`, which on 2026-09-27 was a distribute run: "0 price steps"
 * over a chart showing 7–27 m COP of recovery on the price runs beside it.
 *
 * The honest signal the contract offers is the price-action read this reading already makes per
 * run: a run with at least one price step is a run with a price decision. So `priced` is the
 * newest run (the directory's order) that has one, and `newest` is kept beside it so a screen can
 * say plainly that the newest run carried none. A run with no price step is NOT called a
 * distribute run: a price run that proposed nothing reads exactly the same, and the contract
 * cannot tell the two apart (the `run_kind` ask on bata-platform).
 */
export interface InseasonHeadline {
  /** The directory's first run, whatever kind it is. */
  readonly newest: InseasonRunImpact;
  /** The newest run with at least one price step, or `null` when none of the runs has one. */
  readonly priced: InseasonRunImpact | null;
  /** The run the headline figures are read from: `priced`, else `newest`. */
  readonly shown: InseasonRunImpact;
}

export function inseasonHeadline(runs: readonly InseasonRunImpact[]): InseasonHeadline | null {
  const newest = runs[0];
  if (newest === undefined) return null;
  const priced = runs.find((run) => run.priceSteps > 0) ?? null;
  return { newest, priced, shown: priced ?? newest };
}

// ── exit ────────────────────────────────────────────────────────────────────

/** One consolidation run's impact reading. */
export interface ExitRunImpact {
  readonly runId: string;
  readonly recordedAt: string;
  readonly asOf: string;
  readonly countryKey: string;
  /** `ConsolidationRunSummary.line_count` — every line the run wrote, proposed or withheld. */
  readonly lines: number;
  /**
   * The lane counts below are the lanes the run holds — for a run totalled here, the rows read; for one that was not, the
   * platform's own count of the same lists (`matching` of `GET /exit/consolidation/lines`, ADR 6194). `null` is a count
   * nobody made (a platform that does not state `matching`): absent with a reason, never 0.
   */
  readonly proposedLanes: number | null;
  /** Proposed lanes carrying all three amounts — the lanes every sum below is over, when `totalled`. */
  readonly pricedLanes: number | null;
  /** Proposed lanes missing an amount, and so in none of the sums. Stated so a total reads as partial. */
  readonly unpricedLanes: number | null;
  /**
   * The run's lines were read to the end here and the sums below were made over them. `false`: the run is too large to total in
   * the browser, the sums are absent with a reason, and no row was summed — whatever the lane counts above say.
   */
  readonly totalled: boolean;
  /** Σ `net_benefit_amount` over the priced proposed lanes. */
  readonly netBenefit: ImpactFigure;
  /** Σ `incremental_revenue_amount` over the same lanes. */
  readonly incrementalRevenue: ImpactFigure;
  /**
   * The `revenue_basis` values the priced lanes name, sorted — the basis the revenue (and so
   * the benefit) was valued on. Reference data, shown as it is; an empty list means no lane
   * stated one.
   */
  readonly revenueBases: readonly string[];
}

/**
 * The largest run whose lines this module will total in the browser: the transport's page
 * ceiling times the platform's maximum page size (`BATA_MAXIMUM_PAGE_SIZE`, default 1,000 —
 * measured by exit on 2026-09-24). Past it `collectPages` would refuse the read as unbounded;
 * the figure is then absent with that reason rather than a request that fails.
 */
export const LINE_PAGE_ROWS = 1000;

/** DERIVE — one consolidation run's net benefit, over its proposed lanes. */
export function exitRunImpact(
  run: ConsolidationRunSummary,
  lines: readonly ConsolidationLineSummary[],
): ExitRunImpact {
  const proposed = lines.filter((line) => line.is_proposed);
  const priced = proposed.flatMap((line) => {
    const net = parseDecimal(line.net_benefit_amount);
    const revenue = parseDecimal(line.incremental_revenue_amount);
    const cost = parseDecimal(line.lane_cost_amount);
    return net === null || revenue === null || cost === null
      ? []
      : [{ currency: line.currency_code, net, revenue, basis: line.revenue_basis ?? null }];
  });
  const emptyReason = proposed.length === 0 ? IMPACT_ABSENCE.noProposedLanes : IMPACT_ABSENCE.noPricedLanes;
  return {
    runId: run.run_id,
    recordedAt: run.recorded_at,
    asOf: run.as_of,
    countryKey: run.country_key,
    lines: run.line_count,
    proposedLanes: proposed.length,
    pricedLanes: priced.length,
    unpricedLanes: proposed.length - priced.length,
    totalled: true,
    netBenefit: sumInOneCurrency(priced.map((p) => ({ currency: p.currency, amount: p.net })), emptyReason),
    incrementalRevenue: sumInOneCurrency(priced.map((p) => ({ currency: p.currency, amount: p.revenue })), emptyReason),
    revenueBases: [...new Set(priced.flatMap((p) => (p.basis === null || p.basis.trim() === '' ? [] : [p.basis])))].sort(),
  };
}

/**
 * Where the «Priced lanes» card is listed (ADR 9069): the exit chapter's run-wide lanes page for the run, narrowed to the lanes the
 * card counts — proposed (`is_proposed`) and carrying cost, revenue and net benefit all (`lane_priced`, the platform's own predicate
 * of this very count, ADR 6194). Spelled here because a chapter may not import another; `kpi-links-priced-lanes.test.tsx` holds
 * it equal to the exit chapter's builder.
 *
 * The card is the platform's count of exactly this list (`matching`) whether or not the run was totalled here, so it links at any
 * size of run. `null` only for a count nobody made (`pricedLanes` `null`): an absent figure never links.
 */
export function pricedLanesHref(run: ExitRunImpact): string | null {
  if (run.pricedLanes === null) return null;
  return `/exit/consolidation-run-lanes?runId=${encodeURIComponent(run.runId)}&lanePriced=true&isProposed=true`;
}

/** A run too large to total here — `LINE_PAGE_ROWS` above. */
export function exitRunTooLarge(run: ConsolidationRunSummary, maxPages: number): boolean {
  return run.line_count > maxPages * LINE_PAGE_ROWS;
}

/**
 * The platform's counts of one run's lanes, for a run too large to total here: the proposed lanes, and of them those carrying all
 * three amounts (`lane_priced`) — `matching` of `GET /exit/consolidation/lines` under `is_proposed` and `lane_priced` (ADR 6194).
 * `null`: the platform did not state the count.
 */
export interface ExitLaneCounts { readonly proposed: number | null; readonly priced: number | null }

/**
 * The two questions a run too large to total is asked, each a page of ONE row whose `matching` is the count of the whole list: the
 * proposed lanes, and the proposed lanes that carry cost, revenue and net benefit all (the «Priced lanes» card's own list).
 */
export const EXIT_LANE_COUNT_QUERIES = [
  { is_proposed: true },
  { is_proposed: true, lane_priced: true },
] as const;

/** The lane counts of one run, from the pages of `EXIT_LANE_COUNT_QUERIES` in order. */
export function laneCountsOf(pages: readonly ({ readonly matching?: number | null } | undefined)[]): ExitLaneCounts {
  return { proposed: pages[0]?.matching ?? null, priced: pages[1]?.matching ?? null };
}

/**
 * The reading for a run that was not totalled, and why — the same shape as a totalled one. The sums are absent with `reasonKey`;
 * the lane counts are the platform's (`counts`), true at any size of run, or `null` where nobody counted them: never a false 0.
 */
export function exitRunNotTotalled(
  run: ConsolidationRunSummary,
  reasonKey: string,
  counts: ExitLaneCounts | null = null,
): ExitRunImpact {
  const proposed = counts?.proposed ?? null;
  const priced = counts?.priced ?? null;
  return {
    runId: run.run_id,
    recordedAt: run.recorded_at,
    asOf: run.as_of,
    countryKey: run.country_key,
    lines: run.line_count,
    proposedLanes: proposed,
    pricedLanes: priced,
    unpricedLanes: proposed === null || priced === null ? null : proposed - priced,
    totalled: false,
    netBenefit: absent(reasonKey),
    incrementalRevenue: absent(reasonKey),
    revenueBases: [],
  };
}

// ── the series over runs ────────────────────────────────────────────────────

/**
 * A module's figure across its runs, for the trend chart and its table view.
 *
 * OLDEST FIRST — time runs left to right — though both directories are served newest first.
 *
 * **One currency or no chart.** A bar's length is an amount, and bars in two currencies on
 * one axis compare numbers that are not comparable. Where the measured runs span currencies,
 * `currency` is `null` and `drawable` false; the caller renders the table and says why.
 *
 * An absent figure is a MISSING datum (`not-reported`), which the wrapper draws as a gap and
 * the export writes as a gap — never a zero-height bar.
 */
export interface ImpactSeries {
  /**
   * One category per run, UNIQUE, in time order — the axis label. A run is labelled by its
   * time to the minute; where two runs would read the same, the later one carries its position
   * «(2)» — never a piece of its identifier (audit 2, B). The run's identity is `runIds`, index
   * for index.
   */
  readonly categories: readonly string[];
  readonly data: readonly Datum[];
  /** The run behind each category, index for index — the identity the label stands for. */
  readonly runIds: readonly string[];
  /** The one currency every measured point is in; `null` when there is none or more than one. */
  readonly currency: string | null;
  /** True when at least one point is measured and every measured point shares a currency. */
  readonly drawable: boolean;
}

/** A timestamp to the minute, as a category. Data, not copy — the ISO form every locale reads. */
function minuteOf(timestamp: string): string {
  return timestamp.length >= 16 ? timestamp.slice(0, 16).replace('T', ' ') : timestamp;
}

/**
 * The axis labels for runs already in time order: the minute, and — only where minutes clash — the
 * minute with the run's position, «(2)». Deterministic: the same runs give the same labels in any
 * input order, because the order is fixed by (time, run id) before labelling, so the position is
 * stable. No piece of an identifier is ever a label (audit 2, B); a run listed twice keeps its own
 * slot, so the axis can never carry a duplicate.
 */
export function runLabels(runs: readonly { readonly runId: string; readonly at: string }[]): readonly string[] {
  const seen = new Map<string, number>();
  return runs.map((run) => {
    const label = minuteOf(run.at);
    const count = (seen.get(label) ?? 0) + 1;
    seen.set(label, count);
    return count === 1 ? label : `${label} (${count})`;
  });
}

export function impactSeries(
  runs: readonly { readonly runId: string; readonly at: string; readonly figure: ImpactFigure }[],
): ImpactSeries {
  // Time, then run id: two runs stamped with the same instant have a stable order whatever order
  // the directory served them in, so their labels and bars do not swap between renders.
  const ordered = [...runs].sort((a, b) => (
    a.at < b.at ? -1 : a.at > b.at ? 1 : a.runId < b.runId ? -1 : a.runId > b.runId ? 1 : 0
  ));
  const measured = ordered.flatMap((run) => (run.figure.kind === 'measured' ? [run.figure.currency] : []));
  const currencies = [...new Set(measured)];
  const currency = currencies.length === 1 ? (currencies[0] as string) : null;
  return {
    categories: runLabels(ordered),
    runIds: ordered.map((run) => run.runId),
    data: ordered.map((run): Datum => (
      run.figure.kind === 'measured' ? run.figure.amount : { missing: 'not-reported' }
    )),
    currency,
    drawable: currency !== null,
  };
}

/**
 * Both modules' runs on ONE axis — the Impact page's one visual (Package C6). Every run of either
 * module is a category, in time order (then run id), labelled as `runLabels` labels them; each
 * module is a series with its own runs' figures and a declared gap on the other module's runs
 * (`not-ranged`: the module does not carry that run), and a gap where its own run has no figure
 * (`not-reported`). Drawable only when every measured point shares one currency.
 */
export interface ModuleSeries {
  readonly categories: readonly string[];
  readonly inseason: readonly Datum[];
  readonly exit: readonly Datum[];
  readonly currency: string | null;
  readonly drawable: boolean;
  /** Whether any point has a figure — "nothing measured" is its own sentence. */
  readonly anyMeasured: boolean;
}

export function moduleSeries(
  inseason: readonly { readonly runId: string; readonly at: string; readonly figure: ImpactFigure }[],
  exit: readonly { readonly runId: string; readonly at: string; readonly figure: ImpactFigure }[],
): ModuleSeries {
  const all = [
    ...inseason.map((run) => ({ ...run, module: 'inseason' as const })),
    ...exit.map((run) => ({ ...run, module: 'exit' as const })),
  ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : a.runId < b.runId ? -1 : a.runId > b.runId ? 1 : 0));
  const point = (run: (typeof all)[number], module: 'inseason' | 'exit'): Datum => {
    if (run.module !== module) return { missing: 'not-ranged' };
    return run.figure.kind === 'measured' ? run.figure.amount : { missing: 'not-reported' };
  };
  const currencies = [...new Set(all.flatMap((run) => (run.figure.kind === 'measured' ? [run.figure.currency] : [])))];
  const currency = currencies.length === 1 ? (currencies[0] as string) : null;
  return {
    categories: runLabels(all),
    inseason: all.map((run) => point(run, 'inseason')),
    exit: all.map((run) => point(run, 'exit')),
    currency,
    drawable: currency !== null,
    anyMeasured: currencies.length > 0,
  };
}
